import { useFieldState } from "hooks";
import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faTimes } from "@fortawesome/pro-solid-svg-icons";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import {
  getAllAttributes,
  getDataTypeByPath,
  getPathParts,
  getPropertyDisplayName,
  getType,
  normalisePathParts,
} from "containers/Settings/RuleBooks/Schema/schema";
import { DataTypeMenu, DataTypeMenuProps } from "./DataTypeMenu";

export interface SchemaFieldDataSourceField {
  value: string;
  valueType: string | null;
  source: "FIELD" | "RULESET_FIELD" | "DEPENDENCY_FIELD";
}

export interface SchemaFieldPathPartProps
  extends Omit<DataTypeMenuProps, "anchorEl" | "onClose"> {}

export function SchemaFieldPathPart(props: SchemaFieldPathPartProps) {
  const {
    allowSelectRelations,
    allowSelectManyRelations,
    dataTour,
    depth,
    fieldPath,
    fieldType,
    onSelect,
    pathPart,
    showManyRelations,
    showProperties,
    showRelations,
    schema,
    via,
    readOnly,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const dataType = getDataTypeByPath(
    schema,
    normalisePathParts([fieldPath, via]),
  );

  const [customPath, setCustomPath] = React.useState(dataType ? "" : pathPart);

  const resolvedAttr = dataType
    ? getAllAttributes(dataType).find(attr => attr.via === pathPart)
    : null;

  useEffect(() => {
    setCustomPath(dataType && resolvedAttr ? "" : pathPart);
  }, [setCustomPath, dataType, resolvedAttr, pathPart]);

  const onClickMenuItem = event => {
    setAnchorEl(event.currentTarget);
  };

  const onSubSelect = newValue => {
    setAnchorEl(null);
    onSelect(newValue);
  };

  const onSubClose = () => {
    setAnchorEl(null);
  };

  const acceptCustomPath = event => {
    setAnchorEl(null);
    onSelect(normalisePathParts([via, customPath]));
    setCustomPath(getPathParts(event.target.value)?.[0] || "");
  };

  const isUsingCustomPath = !(resolvedAttr || (!via && !pathPart));
  const unresolvedLabel = pathPart
    ? getPropertyDisplayName(pathPart)
    : "Select a value";

  return (
    <React.Fragment key={`${via}_${pathPart}`}>
      {!isUsingCustomPath ? (
        <Button
          onClick={onClickMenuItem}
          endIcon={!readOnly ? <FontAwesomeIcon icon={faChevronDown} /> : null}
          data-tour={`${dataTour}_SchemaFieldPathPart_${via}_${pathPart}`}
          disabled={readOnly}
        >
          {resolvedAttr ? resolvedAttr.name : unresolvedLabel}
        </Button>
      ) : (
        <TextField
          onKeyDown={event => {
            // Prevent losing focus due to the menu capturing the key event
            event.stopPropagation();
            if (event.key === "Enter") {
              acceptCustomPath(event);
            }
          }}
          onBlur={acceptCustomPath}
          onChange={e => {
            setCustomPath(e.target.value);
          }}
          placeholder="Add a new relation"
          value={customPath}
          data-tour={`${dataTour}_SchemaFieldPathPart_Custom_${via}`}
          disabled={readOnly}
        />
      )}
      <DataTypeMenu
        allowSelectManyRelations={allowSelectManyRelations}
        allowSelectRelations={allowSelectRelations}
        anchorEl={anchorEl}
        dataTour={dataTour}
        depth={depth}
        fieldType={fieldType}
        fieldPath={fieldPath}
        onClose={onSubClose}
        onSelect={onSubSelect}
        pathPart={pathPart}
        schema={schema}
        showManyRelations={showManyRelations}
        showProperties={showProperties}
        showRelations={showRelations}
        via={via}
        readOnly={readOnly}
      />
    </React.Fragment>
  );
}

export interface SchemaFieldDataSourceProps
  extends Omit<
    SchemaFieldPathPartProps,
    "onSelect" | "depth" | "pathPart" | "via"
  > {
  namespace: string;
}

export function SchemaFieldDataSource(props: SchemaFieldDataSourceProps) {
  const {
    allowSelectManyRelations,
    allowSelectRelations,
    dataTour,
    fieldType,
    namespace: ns,
    fieldPath,
    schema,
    showManyRelations,
    showProperties,
    showRelations,
    readOnly,
  } = props;

  const [value, setValue] = useFieldState(ns);

  const dataType = getDataTypeByPath(schema, fieldPath);

  function onAfterViaChanged(newValue) {
    const updatedField = {
      ...value,
      value: newValue,
      valueType: getType(dataType, newValue),
    };
    setValue(updatedField);
  }

  function onClickClearPath() {
    onAfterViaChanged("");
  }

  const pathParts = value?.value ? getPathParts(value.value) : [""];

  return (
    <>
      {pathParts.map((pathPart, index) => {
        const via = index > 0 ? pathParts.slice(0, index).join(".") : "";

        return (
          <SchemaFieldPathPart
            key={via}
            allowSelectManyRelations={allowSelectManyRelations}
            allowSelectRelations={allowSelectRelations}
            dataTour={dataTour}
            depth={index}
            fieldPath={fieldPath}
            fieldType={fieldType}
            onSelect={onAfterViaChanged}
            pathPart={pathPart}
            schema={schema}
            showManyRelations={showManyRelations}
            showProperties={showProperties}
            showRelations={showRelations}
            via={via}
            readOnly={readOnly}
          />
        );
      })}
      {!readOnly && value?.value && (
        <Button
          onClick={onClickClearPath}
          endIcon={<FontAwesomeIcon icon={faTimes} />}
          data-tour={`${dataTour}_Clear`}
        />
      )}
    </>
  );
}
